import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import type { SelectChangeEvent } from "@mui/material/Select";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import type { AreaLocation } from "@/features/deployment-dashboard/types";
import type { LiveViewMode } from "@/features/deployment-dashboard/live-view/components/live-view-tabs";
import {
  LIVE_VIEW_CHART_OPTIONS,
  type LiveViewTimeRange,
} from "@/features/deployment-dashboard/live-view/model/time-range";
import { LayersSelect } from "@/features/deployment-dashboard/shared/layers-select";
import { AreasIcon } from "@/shared/ui/console-icons";
import { SelectWithTooltip } from "@/shared/ui/select-with-tooltip";

/** Shared sizing so top-bar selects never resize as their rendered values change. */
const FIXED_SELECT_SX = {
  width: 180,
  flexShrink: 0,
  "& .MuiSelect-select": {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
};

interface LiveViewFilterBarProps {
  areas: AreaLocation[];
  /** Selected area id to subscribe to. */
  selectedAreaId: string | null;
  onSelectedAreaIdChange: (areaId: string | null) => void;
  showHeatmap: boolean;
  onShowHeatmapChange: (showHeatmap: boolean) => void;
  showSensors: boolean;
  onShowSensorsChange: (showSensors: boolean) => void;
  showDetections: boolean;
  onShowDetectionsChange: (showDetections: boolean) => void;
  showTracks: boolean;
  onShowTracksChange: (showTracks: boolean) => void;
  viewMode: LiveViewMode;
  timeRange: LiveViewTimeRange;
  onTimeRangeChange: (timeRange: LiveViewTimeRange) => void;
}

/**
 * Live view's filter controls, rendered inline in the shared console top bar (via
 * `useTopBarFilters`): lets operators scope the live tracks subscription to specific areas
 * (or every area, via the "All" option) and choose the visible facility schematic layers
 * (heatmap, tracks, sensors) via dropdowns.
 */
export function LiveViewFilterBar({
  areas,
  selectedAreaId,
  onSelectedAreaIdChange,
  showHeatmap,
  onShowHeatmapChange,
  showSensors,
  onShowSensorsChange,
  showDetections,
  onShowDetectionsChange,
  showTracks,
  onShowTracksChange,
  viewMode,
  timeRange,
  onTimeRangeChange,
}: LiveViewFilterBarProps) {
  function handleAreaSelectionChange(event: SelectChangeEvent<string>) {
    const { value } = event.target;
    onSelectedAreaIdChange(value || null);
  }

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: "max-content" }}>
      <SelectWithTooltip
        tooltipTitle="Area"
        aria-label="Area"
        size="small"
        value={selectedAreaId ?? ""}
        onChange={handleAreaSelectionChange}
        startAdornment={<AreasIcon sx={{ fontSize: "0.875rem", mr: 1, color: "text.secondary" }} />}
        sx={FIXED_SELECT_SX}
      >
        {areas.map((area) => (
          <MenuItem key={area.id} value={area.id}>
            {area.name}
          </MenuItem>
        ))}
      </SelectWithTooltip>

      {viewMode === "chart" && (
        <SelectWithTooltip
          tooltipTitle="Time range"
          aria-label="Time range"
          size="small"
          value={timeRange}
          onChange={(event) => onTimeRangeChange(event.target.value as LiveViewTimeRange)}
          startAdornment={
            <AccessTimeOutlinedIcon sx={{ fontSize: "0.875rem", mr: 1, color: "text.secondary" }} />
          }
          sx={{ ...FIXED_SELECT_SX, width: 160 }}
        >
          {LIVE_VIEW_CHART_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </SelectWithTooltip>
      )}

      {viewMode === "map" && (
        <LayersSelect
          showHeatmap={showHeatmap}
          onShowHeatmapChange={onShowHeatmapChange}
          showSensors={showSensors}
          onShowSensorsChange={onShowSensorsChange}
          showDetections={showDetections}
          onShowDetectionsChange={onShowDetectionsChange}
          showTracks={showTracks}
          onShowTracksChange={onShowTracksChange}
        />
      )}
    </Box>
  );
}
